import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../App";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../hooks/Method";
import Barcode from "react-barcode/lib/react-barcode";

const GeneralPrinterReceipt = ({ data }) => {
  const { settings, selStore, stores } = useContext(AppContext);
  const [store, setStore] = useState({});
  const { t } = useTranslation();

  useEffect(() => {
    setStore({ ...stores.find((j) => j.id === selStore) });
    return () => {};
  }, [selStore, stores]);

  return data.id && store.id ? (
    <div
      style={{
        maxWidth: "210mm", // A4 width
        width: "100%",
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontSize: "14px",
        lineHeight: 1.6,
        color: "#333",
        backgroundColor: "#fff",
        padding: "20px",
        margin: "0 auto", // Center the content
        boxSizing: "border-box",
      }}
    >
      {/* Store Header */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "8px",
          paddingBottom: "12px",
          borderBottom: "2px solid #ddd",
        }}
      >
        {store.logo && store.showLogoOnReceipt === true && (
          <div
            style={{
              marginBottom: "15px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={store.logo}
              alt="store_logo"
              style={{
                maxHeight: "120px",
                maxWidth: "250px",
                objectFit: "contain",
              }}
            />
          </div>
        )}
        {store.showStoreNameOnReceipt && (
          <>
            <div
              style={{
                fontSize: "20px",
                fontWeight: "700",
                letterSpacing: "0.5px",
                color: "#222",
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              {store.storeName}
            </div>
            {store.localName && (
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  letterSpacing: "0.5px",
                  color: "#444",
                  marginBottom: "6px",
                  textAlign: "center",
                }}
              >
                {store.localName}
              </div>
            )}
          </>
        )}
        {store.showTaglineOnReceipt && (
          <div
            style={{
              fontSize: "16px",
              fontWeight: "500",
              fontStyle: "italic",
              letterSpacing: "0.5px",
              color: "#444",
              padding: "0px 10px",
              border: "2px dashed #ddd",
              textAlign: "center",
              width: "fit-content",
              margin: "0 auto",
            }}
          >
            {store.tagline}
          </div>
        )}
        {/* ----- Address Section ----- */}
        {(store.showAddressOnReceipt || store.showPhoneOnReceipt) && (
          <div
            style={{
              display: "grid",
              justifyContent: "center",
              fontSize: "15px",
              color: "#666",
              lineHeight: 1.4,
              marginTop: "8px",
              gap: "4px",
            }}
          >
            <div>
              {store.showAddressOnReceipt && (
                <>
                  {store.address && (
                    <span style={{ whiteSpace: "pre-wrap" }}>
                      📌 {store.address}
                    </span>
                  )}
                  <div>
                    {store.city ? `, ${store.city}` : ""}
                    {store.state ? `, ${store.state}` : ""}
                    {store.zip ? `, ${store.zip}` : ""}
                  </div>
                </>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
              }}
            >
              {store.showPhoneOnReceipt && store.phone && (
                <span>📞 {store.phone}</span>
              )}
              {store.showEmailOnReceipt && store.email && (
                <span>✉️ {store.email}</span>
              )}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              {store.website && <span>🌐 {store.website}</span>}
              {store.socialMedia && (
                <span style={{ whiteSpace: "pre-wrap" }}>
                  {store.socialMedia}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Transaction Details */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px",
          marginBottom: "10px",
        }}
      >
        <div>
          <div style={{ display: "flex", gap: "10px", fontSize: "18px" }}>
            <span style={{ fontWeight: "600" }}>{t("sellReturnId")}</span>
            <span style={{ color: "#333" }}>#{data.id}</span>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <span style={{ fontWeight: "500" }}>{t("invoiceNo")}</span>
            <span style={{ color: "#333" }}>#{data.sellId}</span>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <span style={{ fontWeight: "500" }}>{t("sellDate")}</span>
            <span>{formatDate(data.sell.sellDate)}</span>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <span style={{ fontWeight: "500" }}>{t("refundDate")}</span>
            <span>{formatDate(data.refundDate)}</span>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <span style={{ fontWeight: "500" }}>{t("customer")}:</span>
            <span>
              {data.sell?.cstmr
                ? data.sell.cstmr.fullName
                : t("walkInCustomer")}
            </span>
          </div>
          {data.sell?.cstmr && (
            <div style={{ display: "flex", gap: "10px" }}>
              <span style={{ fontWeight: "500" }}>{t("phone")}:</span>
              <span>{data.sell?.cstmr.phone}</span>
            </div>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          {data.sellId && (
            <Barcode
              value={data.sellId.toString()}
              displayValue={true}
              height={50}
              width={2}
              fontSize={14}
            />
          )}
        </div>
      </div>

      {/* Items Table */}
      <div
        style={{
          marginBottom: "10px",
          borderBottom: "2px solid #ddd",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "0.10fr 2.4fr 0.50fr 1fr 1fr",
            gap: "12px",
            fontWeight: "600",
            textTransform: "uppercase",
            marginBottom: "12px",
            color: "#555",
            borderTop: "1px solid #eee",
            borderBottom: "1px solid #eee",
          }}
        >
          <div>#</div>
          <div>{t("itemName")}</div>
          <div style={{ textAlign: "center" }}>{t("qty")}</div>
          <div style={{ textAlign: "right" }}>{t("unitPrice")}</div>
          <div style={{ textAlign: "right" }}>{t("totalPrice")}</div>
        </div>

        {data.items?.map((item, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "0.10fr 2.4fr 0.50fr 1fr 1fr",
              gap: "12px",
              marginBottom: "3px",
              color: "#333",
              paddingBottom: "3px",
              fontSize: "14px",
              borderBottom: `${
                data.items.length > i + 1 ? "1" : "0"
              }px solid #eee`,
            }}
          >
            <div>{i + 1}</div>
            <div>{item.product.label}</div>
            <div style={{ textAlign: "center" }}>{item.returnQty}</div>
            <div style={{ textAlign: "right" }}>
              {settings.curSymbol} {item.price.toFixed(settings.decimal)}
            </div>
            <div style={{ textAlign: "right" }}>
              {settings.curSymbol}{" "}
              {(item.returnQty * item.price).toFixed(settings.decimal)}
            </div>
          </div>
        ))}
      </div>

      {/* Totals Section */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
        }}
      >
        <div></div>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#555" }}>{t("refund")}:</span>
            <span style={{ color: "#333" }}>
              {settings.curSymbol} {data.refundAmt.toFixed(settings.decimal)}
            </span>
          </div>

          {data.cutDiscount > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#555" }}>{t("discount")}:</span>
              <span style={{ color: "#333" }}>
                (-) {settings.curSymbol}{" "}
                {data.cutDiscount.toFixed(settings.decimal)}
              </span>
            </div>
          )}

          {data.refundTaxAmt > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#555" }}>{t("tax")}:</span>
              <span style={{ color: "#333" }}>
                (+) {settings.curSymbol}{" "}
                {data.refundTaxAmt.toFixed(settings.decimal)}
              </span>
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: "600",
              paddingTop: "12px",
              borderTop: "1px solid #ddd",
              color: "#222",
            }}
          >
            <span>
              {data.keepRefundAsCredit ? t("credited") : t("payableRefund")}:
            </span>
            <span>
              {settings.curSymbol}{" "}
              {data.payableRefund.toFixed(settings.decimal)}
            </span>
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

export default GeneralPrinterReceipt;
